/**
 * Reported in: the agent turns round to face them (the not-yet shot) and
 * says well done on this mission. Shown once the debrief is filed, before the
 * key. While locks are still shut it counts them down ("3 down, 4 more to go")
 * and asks when the next mission should come: Now, Tomorrow, or a time they
 * Schedule. Once the vault is open it's a single button, and a tap anywhere
 * moves on too.
 */
import { useEffect, useState, type CSSProperties } from "react";
import { LOCK_WORDS } from "./vault-clips";
import { ACTION_BTN_CLASS } from "./step-shell";
import { SchedulePicker } from "./schedule-picker";
import { PHOTO_BOX, bubbleFor, notYetFor } from "./desk-art";

/** When the next mission comes: right away, the step's own timing
 *  (tomorrow), or a time they picked (ms epoch). */
export type NextMission = "now" | "tomorrow" | number;

/** The vault's locks — one per mission, until it opens. */
const LOCKS = LOCK_WORDS.length;

const ORDINAL = [
  "first",
  "second",
  "third",
  "fourth",
  "fifth",
  "sixth",
  "seventh",
  "eighth",
  "ninth",
  "tenth",
];

/** What the agent says once a mission is reported. The first seven each
 *  open one of the vault's locks, so each is praised for its own truth;
 *  after that, they are counted. 1–6 leave the "when" to the buttons. */
const PRAISE = [
  "Mission one: done. Most people never take the first step. You just did. That's Brave, and the first lock on your door is open.",
  "Two for two. You said you would, and you did. That's Reliable, and it's the rarest thing there is. Another lock open.",
  "You picked your mark and you hit it. That's Aim.",
  "You reached out and someone reached back. That's Connect, and it's the whole point.",
  "Somebody's day went differently because of you. That's Influence, and it shows.",
  "You stood up for someone. That's Champion.",
  "Seven missions. Seven locks. You didn't quit when it got ordinary. That's Outlast, and the vault is open. Tomorrow we talk about the big one.",
];
const praiseFor = (n: number) => {
  if (n >= 1 && n <= PRAISE.length) return PRAISE[n - 1];
  const which = n <= ORDINAL.length ? ORDINAL[n - 1] : `${n}th`;
  return `That's your ${which} mission in the file, and it shows. Proud of you, operative. Your next one comes by text tomorrow.`;
};

const CHOICE_BTN_CLASS = `${ACTION_BTN_CLASS} flex-1 rounded-[9px] px-3 py-3 text-[15px] font-bold`;

export function WellDone({
  handler,
  missionNo,
  message,
  button,
  nextLock,
  onGo,
}: {
  handler: string | null;
  /** the mission just reported (1 = their first) */
  missionNo: number;
  /** authored copy; otherwise well done on this mission */
  message?: string;
  /** the single button once the vault is open */
  button: string;
  /** the lock tomorrow's mission opens, named before asking when */
  nextLock?: string;
  onGo: (next: NextMission) => void;
}) {
  const bubble = bubbleFor(handler);
  // locks still shut: they choose when the next mission comes
  const choosing = missionNo < LOCKS;
  // Schedule: when our picker was opened (0 = closed)
  const [openedAt, setOpenedAt] = useState(0);
  // Straight after the key's name modal: if the phone keyboard left the
  // page scrolled, iOS places every tap off by that much (Now did nothing).
  useEffect(() => {
    window.scrollTo(0, 0);
    const t = window.setTimeout(() => window.scrollTo(0, 0), 400);
    return () => window.clearTimeout(t);
  }, []);

  return (
    <div
      onClick={choosing ? undefined : () => onGo("tomorrow")}
      style={{ "--sm-bg": "#000" } as CSSProperties}
      className="fixed inset-0 z-[40] animate-[smFadeIn_.5s_ease_both] overflow-clip bg-black font-serif"
    >
      <img
        src={notYetFor(handler)}
        alt=""
        className="absolute inset-0 h-full w-full object-cover object-[50%_100%]"
      />
      <div
        style={PHOTO_BOX}
        className="absolute bottom-0 left-1/2 -translate-x-1/2"
      >
        <div
          // a little lower than where the agent speaks elsewhere: this one
          // carries the choice of when, and sits clear of their face
          style={{ top: `calc(${bubble.top} + 5%)` }}
          className="absolute right-0 left-0 flex flex-col items-center"
        >
          <div className="relative w-[min(380px,calc(100vw-40px))] animate-[smSay_.45s_cubic-bezier(.2,.8,.3,1)_.3s_both] rounded-[6px] bg-[#f3ecd8] px-5 py-4 text-center text-[#2b2107] shadow-[0_22px_50px_rgba(0,0,0,.75)]">
            {handler ? (
              <div className="mb-1.5 font-mono text-[10px] tracking-[.24em] text-[#8a7350] uppercase">
                {handler}
              </div>
            ) : null}
            <div className="text-[clamp(14px,4.1vw,17px)] leading-[1.32] font-medium text-pretty">
              {message || praiseFor(missionNo)}
            </div>
            {choosing ? (
              <>
                <div className="mt-2.5 font-stencil text-[clamp(16px,4.6vw,19px)] tracking-[.06em] text-[#a8271c] uppercase">
                  {missionNo} down, {LOCKS - missionNo} more to go
                </div>
                {nextLock ? (
                  <div className="mt-1 font-mono text-[11px] tracking-[.2em] text-[#8a7350] uppercase">
                    Tomorrow opens {nextLock}
                  </div>
                ) : null}
                <div className="mt-1.5 text-[clamp(14px,4.1vw,17px)] leading-[1.32] font-medium">
                  When would you like your next mission, agent?
                </div>
              </>
            ) : null}
            <div
              style={{ left: bubble.tailLeft }}
              className="absolute -top-[26px] size-0 -translate-x-1/2 border-x-[11px] border-b-[27px] border-x-transparent border-b-[#f3ecd8]"
            />
          </div>

          {choosing ? (
            <div className="mt-4 w-[min(380px,calc(100vw-40px))] animate-[smSay_.45s_cubic-bezier(.2,.8,.3,1)_.7s_both]">
              <div className="flex gap-2">
                <button
                  type="button"
                  onClick={() => onGo("now")}
                  className={CHOICE_BTN_CLASS}
                >
                  Now
                </button>
                <button
                  type="button"
                  onClick={() => onGo("tomorrow")}
                  className={CHOICE_BTN_CLASS}
                >
                  Tomorrow
                </button>
                <button
                  type="button"
                  aria-expanded={openedAt > 0}
                  onClick={() => setOpenedAt((t) => (t ? 0 : Date.now()))}
                  className={CHOICE_BTN_CLASS}
                >
                  Schedule
                </button>
              </div>
            </div>
          ) : (
            <button
              type="button"
              onClick={(e) => {
                // the whole screen already goes on with a tap: once
                e.stopPropagation();
                onGo("tomorrow");
              }}
              className={`mt-4 ${ACTION_BTN_CLASS} animate-[smSay_.45s_cubic-bezier(.2,.8,.3,1)_.7s_both] rounded-[9px] px-10 py-3 text-[16px] font-bold`}
            >
              {button}
            </button>
          )}
        </div>
      </div>
      {/* the picker, over the whole screen */}
      {openedAt ? (
        <SchedulePicker
          key={openedAt}
          from={openedAt}
          // (the picker only sets a time still ahead, checked as they tap)
          // the picker closes on Set: what follows (their number, then the
          // confirmation) must not open behind it
          onSet={(at) => {
            setOpenedAt(0);
            onGo(at);
          }}
          onClose={() => setOpenedAt(0)}
        />
      ) : null}
      <style>{`
        @keyframes smFadeIn{from{opacity:0}to{opacity:1}}
        @keyframes smSay{from{opacity:0;transform:translateY(10px) scale(.96)}to{opacity:1;transform:none}}
      `}</style>
    </div>
  );
}
